import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { WaiverSection } from '../../types/waiver';
import axios from 'axios';
import { API_URL } from '../../constants/api';

const SectionContainer = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
  background: ${({ theme, $isDragging }) => $isDragging ? theme.colors.surfaceHover : theme.colors.surface};
  border: 1px solid ${({ theme, $isDragOver, $isDragging }) =>
    $isDragOver ? theme.colors.primary :
    $isDragging ? theme.colors.primary :
    theme.colors.border};
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  position: relative;
  opacity: ${({ $isDragging }) => $isDragging ? 0.5 : 1};
  transition: border-color 0.2s ease, opacity 0.2s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SectionType = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  &.delete:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  font-size: 12px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ListItemContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  align-items: center;
`;

const ListItemInput = styled(Input)`
  flex: 1;
`;

const AddItemButton = styled.button`
  padding: 5px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    opacity: 0.9;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 2px solid ${({ theme }) => theme.colors.border};
  margin: 16px 0;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 4px;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const ImageUploadBox = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }
`;

const UploadIcon = styled.span`
  font-size: 32px;
  margin-bottom: 8px;
`;

const UploadText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  &:hover {
    opacity: 0.9;
  }
`;

const UploadingText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  padding: 20px;
  text-align: center;
`;

interface WaiverSectionEditorProps {
  section: WaiverSection;
  index: number;
  onChange: (section: WaiverSection) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDragEnd: () => void;
  isDragging: boolean;
  isDragOver: boolean;
}

const sectionTypeLabels: Record<string, string> = {
  heading: 'Heading',
  rich_text: 'Rich Text',
  image: 'Image',
  divider: 'Divider',
  bullet_list: 'Bullet List',
  numbered_list: 'Numbered List',
};

const WaiverSectionEditor: React.FC<WaiverSectionEditorProps> = ({
  section,
  index,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
  isDragOver,
}) => {
  const [items, setItems] = useState<string[]>(section.items || []);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContentChange = (content: string) => {
    onChange({ ...section, content });
  };

  const handleHeadingLevelChange = (level: number) => {
    onChange({ ...section, headingLevel: level });
  };

  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
    onChange({ ...section, items: newItems });
  };

  const handleAddItem = () => {
    const newItems = [...items, ''];
    setItems(newItems);
    onChange({ ...section, items: newItems });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    onChange({ ...section, items: newItems });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid image format. Allowed: JPEG, PNG, GIF, WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('imageFile', file);

      const fieldToken = localStorage.getItem('fieldToken');
      const response = await axios.post(`${API_URL}/api/fieldnews/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${fieldToken}`,
        },
      });

      const imageUrl = response.data.imageUrl;
      handleContentChange(imageUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    handleContentChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderContent = () => {
    switch (section.type) {
      case 'heading':
        // For headings: headingLevel = size (1-4), items[0] = alignment ('left', 'center', 'right')
        const headingAlign = section.items?.[0] || 'left';
        return (
          <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <Select
                value={section.headingLevel}
                onChange={(e) => handleHeadingLevelChange(Number(e.target.value))}
              >
                <option value={1}>H1 - Large</option>
                <option value={2}>H2 - Medium</option>
                <option value={3}>H3 - Small</option>
                <option value={4}>H4 - Extra Small</option>
              </Select>
              <Select
                value={headingAlign}
                onChange={(e) => onChange({ ...section, items: [e.target.value] })}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </div>
            <Input
              type="text"
              value={section.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Enter heading text..."
            />
          </>
        );

      case 'rich_text':
        return (
          <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
              <span style={{ color: '#888', fontSize: '13px' }}>Align:</span>
              <Select
                value={section.headingLevel}
                onChange={(e) => handleHeadingLevelChange(Number(e.target.value))}
              >
                <option value={1}>Left</option>
                <option value={2}>Center</option>
                <option value={3}>Right</option>
              </Select>
            </div>
            <TextArea
              value={section.content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder="Enter text content..."
            />
          </>
        );

      case 'image':
        if (isUploading) {
          return <UploadingText>Uploading image...</UploadingText>;
        }

        if (section.content) {
          // Parse size from headingLevel (reusing field: 1=small, 2=medium, 3=large, 4=full)
          const sizeMap: Record<number, string> = { 1: '150px', 2: '250px', 3: '400px', 4: '100%' };
          const imageSize = sizeMap[section.headingLevel] || '250px';

          return (
            <>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                <span style={{ color: '#888', fontSize: '13px' }}>Size:</span>
                <Select
                  value={section.headingLevel}
                  onChange={(e) => handleHeadingLevelChange(Number(e.target.value))}
                >
                  <option value={1}>Small (150px)</option>
                  <option value={2}>Medium (250px)</option>
                  <option value={3}>Large (400px)</option>
                  <option value={4}>Full Width</option>
                </Select>
              </div>
              <ImagePreviewContainer>
                <PreviewImage src={section.content} alt="Waiver section" style={{ maxWidth: imageSize }} />
                <RemoveImageButton onClick={handleRemoveImage} title="Remove image">
                  ✕
                </RemoveImageButton>
              </ImagePreviewContainer>
            </>
          );
        }

        return (
          <>
            <ImageUploadBox htmlFor={`image-upload-${section.id}`}>
              <UploadIcon>📷</UploadIcon>
              <UploadText>Click to upload image (max 5MB)</UploadText>
              <UploadText style={{ fontSize: '12px', marginTop: '4px' }}>
                JPEG, PNG, GIF, WebP
              </UploadText>
            </ImageUploadBox>
            <HiddenInput
              ref={fileInputRef}
              id={`image-upload-${section.id}`}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
            />
          </>
        );

      case 'divider':
        return <Divider />;

      case 'bullet_list':
      case 'numbered_list':
        return (
          <>
            {items.map((item, index) => (
              <ListItemContainer key={index}>
                <span style={{ color: '#888', minWidth: '24px' }}>
                  {section.type === 'bullet_list' ? '•' : `${index + 1}.`}
                </span>
                <ListItemInput
                  type="text"
                  value={item}
                  onChange={(e) => handleItemChange(index, e.target.value)}
                  placeholder="Enter list item..."
                />
                <IconButton className="delete" onClick={() => handleRemoveItem(index)}>
                  ✕
                </IconButton>
              </ListItemContainer>
            ))}
            <AddItemButton onClick={handleAddItem}>+ Add Item</AddItemButton>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <SectionContainer
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDragEnd={onDragEnd}
      $isDragging={isDragging}
      $isDragOver={isDragOver}
    >
      <SectionHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <DragHandle>⋮⋮</DragHandle>
          <SectionType>{sectionTypeLabels[section.type] || section.type}</SectionType>
        </div>
        <ButtonGroup>
          <IconButton onClick={onMoveUp} disabled={isFirst} title="Move Up">
            ↑
          </IconButton>
          <IconButton onClick={onMoveDown} disabled={isLast} title="Move Down">
            ↓
          </IconButton>
          <IconButton className="delete" onClick={onDelete} title="Delete">
            🗑
          </IconButton>
        </ButtonGroup>
      </SectionHeader>
      {renderContent()}
    </SectionContainer>
  );
};

export default WaiverSectionEditor;
